/**
 * Keeps BIM geometry in the browser across reloads, and big floors' full element pages for a few minutes,
 * through the geometry_cache_sw.js service worker (see there for what it keeps). On acceptance it took a
 * 1.34M-element floor from ~43 s to ~24 s to load again. Customer data is kept on the user's machine, so it's
 * dropped on sign out and when the user leaves the project.
 */
const SCRIPT_URL = "/geometry_cache_sw.js";

const serviceWorkers = (): ServiceWorkerContainer | null => (typeof navigator !== "undefined" && navigator.serviceWorker) || null;

export async function enableGeometryCache() {
  try {
    await serviceWorkers()?.register(SCRIPT_URL, { scope: "/" });
  } catch (error) {
    // Unsupported (eg: some private windows): geometry just loads from the network as before.
    // eslint-disable-next-line no-console
    console.warn("[geometry_cache] not available", error);
  }
}

/** Unregisters the cache and drops everything it kept. */
export async function disableGeometryCache() {
  await clearGeometryCache();
  await (await getRegistration())?.unregister();
}

/** The worker's caches (see geometry_cache_sw.js): geometry, and big floors' element pages. */
const CACHE_PREFIXES = ["avvir-geometry:", "avvir-elements:"];

/**
 * Drops everything the cache kept. Deleted from the page rather than by the worker: a worker that's about to be
 * unregistered, or isn't running, may never get to it, and this is customer data.
 */
export async function clearGeometryCache() {
  try {
    const cacheStorage = typeof caches !== "undefined" ? caches : null;
    const cacheNames = await cacheStorage?.keys() ?? [];
    await Promise.all(cacheNames.filter(name => CACHE_PREFIXES.some(prefix => name.startsWith(prefix))).map(name => cacheStorage.delete(name)));
  } catch {
    // No Cache Storage (eg: some private windows): nothing was kept.
  }
}

/** Drops geometry kept for any other project. */
export async function keepGeometryCacheForProject(projectId: string) {
  await postToCache({ type: "keepProject", projectId });
}

/**
 * Has the worker fetch these element pages into its cache without parsing them here, so a later request for
 * them (eg: entering Deviation Analysis) doesn't wait on the gateway.
 */
export async function prefetchElementPages(urls: string[], headers: Record<string, string>) {
  await postToCache({ type: "prefetchElements", urls, headers });
}

/** Drops a floor's cached element pages (eg: after its elements are edited). */
export async function dropCachedElementPages(projectId: string, floorId: string) {
  await postToCache({ type: "dropElements", projectId, floorId });
}

async function getRegistration(): Promise<ServiceWorkerRegistration | null> {
  try {
    const registrations = await serviceWorkers()?.getRegistrations() ?? [];
    return registrations.find(registration => registration.active?.scriptURL.endsWith(SCRIPT_URL)) ?? null;
  } catch {
    return null;
  }
}

async function postToCache(message: { type: string, projectId?: string, floorId?: string, urls?: string[], headers?: Record<string, string> }) {
  (await getRegistration())?.active?.postMessage(message);
}
